import type { Tag } from '@anytracker/core'
import { useEffect, useRef, useState } from 'react'

import { Button } from '../../components/Button.js'
import { Chip } from '../../components/Chip.js'
import styles from './EntryForm.module.css'

interface Props {
  readonly tags: readonly Tag[]
  readonly selected: readonly string[]
  readonly onToggle: (tagId: string) => void
  readonly onCreate: (name: string) => Promise<void>
}

/**
 * Tags, creatable without leaving the form.
 *
 * The acceptance criterion, and it earns its place: a tag that requires a trip
 * to another screen gets skipped, and the entry loses the context that made it
 * worth recording. Free-text tagging produces near-duplicates within weeks,
 * which is what `TagRepository.merge` is for (T-107) — the fix belongs there,
 * not in friction here.
 */
export function TagPicker({ tags, selected, onToggle, onCreate }: Props) {
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const input = useRef<HTMLInputElement>(null)

  /*
   * Focus the field when it appears, not on page load.
   *
   * `autoFocus` would be wrong — it moves focus without the user asking, which
   * is why the lint rule forbids it. This fires only in response to their tap
   * on "New tag", where landing in the field is exactly what they expect and
   * saves the extra tap this ticket exists to eliminate.
   */
  useEffect(() => {
    if (creating) {
      input.current?.focus()
    }
  }, [creating])

  async function create() {
    const trimmed = name.trim()
    if (trimmed === '') {
      return
    }
    setName('')
    setCreating(false)
    await onCreate(trimmed)
  }

  return (
    <>
      <div className={styles['tags']}>
        {tags.map((tag) => (
          <Chip
            key={tag.id}
            {...(tag.colour === undefined ? {} : { colour: tag.colour })}
            selected={selected.includes(tag.id)}
            onClick={() => onToggle(tag.id)}
          >
            {tag.name}
          </Chip>
        ))}
        {!creating && <Chip onClick={() => setCreating(true)}>+ New tag</Chip>}
      </div>

      {creating && (
        <div className={styles['newTag']}>
          <input
            ref={input}
            className={styles['newTagField']}
            aria-label="New tag name"
            value={name}
            placeholder="Tag name"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void create()
              }
              if (event.key === 'Escape') {
                setCreating(false)
                setName('')
              }
            }}
          />
          <Button variant="primary" onClick={() => void create()}>
            Add
          </Button>
        </div>
      )}
    </>
  )
}
